import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  EmojiEvents,
  Star,
  TrendingUp,
  Leaderboard,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface GamificationManagementProps {
  courseId: string;
}

const GamificationManagement: React.FC<GamificationManagementProps> = ({ courseId }) => {
  const [tabValue, setTabValue] = useState(0);
  const [badgeDialogOpen, setBadgeDialogOpen] = useState(false);
  const [pointsDialogOpen, setPointsDialogOpen] = useState(false);
  const [levelDialogOpen, setLevelDialogOpen] = useState(false);
  const [leaderboardDialogOpen, setLeaderboardDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Mock data
  const badges = [
    { id: 1, name: 'Perfect Score', description: 'Get 100% on any quiz', icon: '⭐', earnedCount: 45, earnRate: 18.4 },
    { id: 2, name: 'Speed Learner', description: 'Complete topic in record time', icon: '⚡', earnedCount: 32, earnRate: 13.1 },
    { id: 3, name: 'Streak Master', description: 'Study for 7 consecutive days', icon: '🔥', earnedCount: 28, earnRate: 11.4 },
    { id: 4, name: 'Course Conqueror', description: 'Complete entire course', icon: '🏆', earnedCount: 12, earnRate: 4.9 },
  ];

  const pointConfiguration = {
    videoWatched: 10,
    quizCompleted: 25,
    perfectQuizScore: 50,
    assignmentSubmitted: 30,
    topicCompleted: 40,
    courseCompleted: 200,
    dailyLogin: 5,
  };

  const xpLevels = [
    { level: 1, levelName: 'Beginner', xpThreshold: 0, studentsAtLevel: 45 },
    { level: 2, levelName: 'Learner', xpThreshold: 500, studentsAtLevel: 68 },
    { level: 3, levelName: 'Student', xpThreshold: 1500, studentsAtLevel: 52 },
    { level: 4, levelName: 'Scholar', xpThreshold: 3000, studentsAtLevel: 38 },
    { level: 5, levelName: 'Expert', xpThreshold: 5000, studentsAtLevel: 22 },
    { level: 6, levelName: 'Master', xpThreshold: 8000, studentsAtLevel: 12 },
    { level: 7, levelName: 'Grandmaster', xpThreshold: 12000, studentsAtLevel: 5 },
  ];

  const leaderboards = [
    { id: 1, name: 'Class Leaderboard', type: 'class', rankingMetric: 'XP', participantCount: 189, enabled: true },
    { id: 2, name: 'School Leaderboard', type: 'school', rankingMetric: 'Quiz Scores', participantCount: 245, enabled: true },
    { id: 3, name: 'Global Leaderboard', type: 'global', rankingMetric: 'Completion Speed', participantCount: 245, enabled: false },
  ];

  const badgeEarningData = badges.map(badge => ({
    name: badge.name,
    earned: badge.earnedCount,
  }));

  const levelDistribution = xpLevels.map(level => ({
    level: level.levelName,
    students: level.studentsAtLevel,
  }));

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, item: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Gamification Management
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Configure badges, points, XP levels, and leaderboards to enhance student engagement
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Badges" />
          <Tab label="Points" />
          <Tab label="XP Levels" />
          <Tab label="Leaderboards" />
          <Tab label="Analytics" />
        </Tabs>
      </Paper>

      {/* Badges Tab */}
      {tabValue === 0 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Course Badges</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setSelectedItem(null);
                setBadgeDialogOpen(true);
              }}
            >
              Add Badge
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Icon</TableCell>
                  <TableCell>Badge Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Earned</TableCell>
                  <TableCell align="right">Earn Rate</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {badges.map((badge) => (
                  <TableRow key={badge.id}>
                    <TableCell>{badge.icon}</TableCell>
                    <TableCell>{badge.name}</TableCell>
                    <TableCell>{badge.description}</TableCell>
                    <TableCell align="right">{badge.earnedCount}</TableCell>
                    <TableCell align="right">{badge.earnRate}%</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, badge)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Points Tab */}
      {tabValue === 1 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Point Configuration</Typography>
            <Button
              variant="outlined"
              onClick={() => setPointsDialogOpen(true)}
            >
              Edit Points
            </Button>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Current Point Values
                </Typography>
                <Box mt={2}>
                  {Object.entries(pointConfiguration).map(([key, value]) => (
                    <Box key={key} display="flex" justifyContent="space-between" mb={2}>
                      <Typography variant="body2">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {value} points
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Point Distribution
                </Typography>
                <Box mt={2}>
                  <Typography variant="body2" color="text.secondary">
                    Total Points Awarded: <strong>45,230</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    Average Points per Student: <strong>184.6</strong>
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* XP Levels Tab */}
      {tabValue === 2 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">XP Level Configuration</Typography>
            <Button
              variant="outlined"
              onClick={() => setLevelDialogOpen(true)}
            >
              Edit Levels
            </Button>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Level Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={levelDistribution}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="level" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="students" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Level Details
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Level</TableCell>
                        <TableCell align="right">XP</TableCell>
                        <TableCell align="right">Students</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {xpLevels.map((level) => (
                        <TableRow key={level.level}>
                          <TableCell>{level.levelName}</TableCell>
                          <TableCell align="right">{level.xpThreshold}</TableCell>
                          <TableCell align="right">{level.studentsAtLevel}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Leaderboards Tab */}
      {tabValue === 3 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Leaderboards</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setSelectedItem(null);
                setLeaderboardDialogOpen(true);
              }}
            >
              Add Leaderboard
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Ranking Metric</TableCell>
                  <TableCell align="right">Participants</TableCell>
                  <TableCell align="right">Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leaderboards.map((leaderboard) => (
                  <TableRow key={leaderboard.id}>
                    <TableCell>{leaderboard.name}</TableCell>
                    <TableCell>
                      <Chip label={leaderboard.type} size="small" />
                    </TableCell>
                    <TableCell>{leaderboard.rankingMetric}</TableCell>
                    <TableCell align="right">{leaderboard.participantCount}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={leaderboard.enabled ? 'Enabled' : 'Disabled'}
                        color={leaderboard.enabled ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, leaderboard)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Analytics Tab */}
      {tabValue === 4 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Badge Earning Statistics
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={badgeEarningData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="earned" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Level Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={levelDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ level, percent }) => `${level} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="students"
                  >
                    {levelDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={['#82ca9d', '#8884d8', '#ffc658', '#ff7300', '#ff6b6b', '#8e44ad', '#3498db'][index]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Badge Dialog */}
      <Dialog open={badgeDialogOpen} onClose={() => setBadgeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedItem ? 'Edit Badge' : 'Add New Badge'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField label="Badge Name" fullWidth defaultValue={selectedItem?.name || ''} />
            <TextField label="Description" fullWidth multiline rows={2} defaultValue={selectedItem?.description || ''} />
            <TextField label="Icon (emoji)" fullWidth defaultValue={selectedItem?.icon || ''} />
            <FormControl fullWidth>
              <InputLabel>Criteria Type</InputLabel>
              <Select label="Criteria Type" defaultValue="quiz_score">
                <MenuItem value="quiz_score">Quiz Score</MenuItem>
                <MenuItem value="completion_time">Completion Time</MenuItem>
                <MenuItem value="streak">Study Streak</MenuItem>
                <MenuItem value="topic_completion">Topic Completion</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Threshold" type="number" fullWidth defaultValue={selectedItem?.threshold || ''} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBadgeDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setBadgeDialogOpen(false)}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Points Dialog */}
      <Dialog open={pointsDialogOpen} onClose={() => setPointsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Point Values</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            {Object.entries(pointConfiguration).map(([key, value]) => (
              <TextField
                key={key}
                label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                type="number"
                fullWidth
                defaultValue={value}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPointsDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setPointsDialogOpen(false)}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Level Dialog */}
      <Dialog open={levelDialogOpen} onClose={() => setLevelDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit XP Levels</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Configure XP thresholds for each level
            </Typography>
            {xpLevels.map((level) => (
              <Box key={level.level} display="flex" gap={2} alignItems="center">
                <TextField
                  label={`Level ${level.level} - ${level.levelName}`}
                  type="number"
                  defaultValue={level.xpThreshold}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Level Name"
                  defaultValue={level.levelName}
                  sx={{ flex: 1 }}
                />
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLevelDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setLevelDialogOpen(false)}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Leaderboard Dialog */}
      <Dialog open={leaderboardDialogOpen} onClose={() => setLeaderboardDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedItem ? 'Edit Leaderboard' : 'Add New Leaderboard'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField label="Leaderboard Name" fullWidth defaultValue={selectedItem?.name || ''} />
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select label="Type" defaultValue={selectedItem?.type || 'class'}>
                <MenuItem value="class">Class</MenuItem>
                <MenuItem value="school">School</MenuItem>
                <MenuItem value="global">Global</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Ranking Metric</InputLabel>
              <Select label="Ranking Metric" defaultValue={selectedItem?.rankingMetric || 'XP'}>
                <MenuItem value="XP">XP</MenuItem>
                <MenuItem value="Quiz Scores">Quiz Scores</MenuItem>
                <MenuItem value="Completion Speed">Completion Speed</MenuItem>
                <MenuItem value="Streak">Streak</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Switch defaultChecked={selectedItem?.enabled ?? true} />}
              label="Enabled"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaderboardDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setLeaderboardDialogOpen(false)}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => {
          if (tabValue === 0) setBadgeDialogOpen(true);
          if (tabValue === 3) setLeaderboardDialogOpen(true);
          handleMenuClose();
        }}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit
        </MenuItem>
        <MenuItem onClick={handleMenuClose} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default GamificationManagement;

